import { useState, useMemo } from 'react'
import { Coffee, ChevronRight, Search } from 'lucide-react'
import { Input } from '@/shared/ui/input'
import { Skeleton } from '@/shared/ui/skeleton'
import { useTelegram } from '@/shared/lib/telegram'
import { useTea } from '@/entities/product/api/use-products'
import { useTeaCategories } from '@/entities/product/api/use-categories'
import { useDrinkHistory } from '@/features/drink-history/api/use-drink-history'
import { useFavorites } from '@/features/favorites/api/use-favorites'
import { cn } from '@/shared/lib/utils'
import { OptimizedImage } from '@/shared/ui'
import type { Tea } from '@/entities/product/model/types'

export interface SelectedTea {
  id: string
  name?: string
  category?: string
  subcategory?: string
  imageUrl?: string
}

export interface TeaSearchContentProps {
  /** Callback при выборе чая */
  onSelect: (tea: SelectedTea) => void
  /** Вариант отображения: sheet (компактный) или page (полноэкранный) */
  variant?: 'sheet' | 'page'
  /** Дополнительные классы */
  className?: string
}

/**
 * Виджет: Контент поиска и выбора чая
 * 
 * Единый компонент для:
 * - BottomSheet на главной ("Хочу записать чай")
 * - Страница добавления записи в дневник
 * 
 * Показывает:
 * - Поиск по названию
 * - Категории с бэкенда
 * - Недавно выпитые чаи
 * - Избранное
 * - Результаты поиска с подкатегориями
 */
export function TeaSearchContent({ 
  onSelect, 
  variant = 'sheet',
  className 
}: TeaSearchContentProps) {
  const { hapticFeedback } = useTelegram()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  // Категории чая с API
  const { data: categoriesData, isLoading: isCategoriesLoading } = useTeaCategories()
  const categories = categoriesData?.data || []
  
  // Недавние записи
  const { data: recentData } = useDrinkHistory({ limit: 5 })
  const recentEntries = recentData?.data || []
  
  // Уникальные недавние чаи (только с teaId)
  const recentTeas = useMemo(() => {
    const seen = new Set<string>()
    return recentEntries
      .filter(entry => {
        if (!entry.teaId || seen.has(entry.teaId)) return false
        seen.add(entry.teaId)
        return true
      })
      .slice(0, 5)
  }, [recentEntries])
  
  // Избранное
  const { data: favoritesData } = useFavorites({ productType: 'tea', limit: 10 })
  const favorites = favoritesData?.data || []
  const favoriteTeas = favorites.filter(fav => fav.product && fav.productType === 'tea')

  // Поиск чая
  const { data: searchData, isLoading: isSearching } = useTea({
    search: searchQuery || undefined,
    category: selectedCategory || undefined,
    limit: 20,
  })
  const searchResults = searchData?.data || []
  
  const handleTeaSelect = (tea: SelectedTea) => {
    hapticFeedback.selectionChanged()
    onSelect(tea)
  }
  
  // Показываем результаты или начальный контент
  const showSearchResults = searchQuery || selectedCategory
  const showRecent = !showSearchResults && recentTeas.length > 0
  const showFavorites = !showSearchResults && favoriteTeas.length > 0

  // Стили в зависимости от варианта
  const containerClass = variant === 'page' 
    ? 'space-y-6' 
    : 'space-y-5'
    
  const inputSize = variant === 'page' ? 'h-12' : 'h-10'
  
  return (
    <div className={cn(containerClass, className)}>
      {/* Поиск */}
      <div className="relative">
        <Search className={cn(
          'absolute left-3 top-1/2 -translate-y-1/2 text-gray-400',
          variant === 'page' ? 'w-5 h-5' : 'w-4 h-4'
        )} />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Поиск по названию..."
          className={cn(
            'w-full pl-10 pr-4',
            inputSize,
            'bg-white dark:bg-dark-card',
            'border border-silk dark:border-dark-border rounded-xl',
            'text-base text-ink dark:text-white',
            'placeholder:text-gray-400',
            'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
            'transition-all'
          )}
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>

      {/* Категории */}
      {!searchQuery && (
        <div>
          <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3">
            Категории
          </h3>
          <div className="flex flex-wrap gap-2">
            {isCategoriesLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-20 rounded-xl" />
              ))
            ) : (
              categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => {
                    hapticFeedback.selectionChanged()
                    setSelectedCategory(selectedCategory === cat.id ? null : cat.id)
                  }}
                  className={cn(
                    'px-4 py-2 rounded-xl text-sm font-medium transition-all',
                    'active:scale-95',
                    selectedCategory === cat.id
                      ? 'bg-primary dark:bg-primary-light text-white shadow-sm'
                      : 'bg-white dark:bg-dark-card text-gray-700 dark:text-gray-300 border border-silk dark:border-dark-border'
                  )}
                >
                  {cat.name}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Недавно выпитые */}
      {showRecent && (
        <div>
          <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3">
            Недавно пили
          </h3>
          <div className="space-y-2">
            {recentTeas.map((entry) => (
              <TeaCard
                key={entry.id}
                name={entry.teaName || entry.tea?.name || 'Чай'}
                category={entry.tea?.categoryName}
                imageUrl={entry.userPhotoUrl || entry.photoUrl || entry.tea?.images?.[0]?.url}
                subtitle={new Date(entry.drankAt).toLocaleDateString('ru-RU', {
                  day: 'numeric',
                  month: 'short',
                })}
                onClick={() => handleTeaSelect({
                  id: entry.teaId!,
                  name: entry.teaName || entry.tea?.name,
                  category: entry.tea?.categoryName,
                  imageUrl: entry.userPhotoUrl || entry.photoUrl || entry.tea?.images?.[0]?.url,
                })}
              />
            ))}
          </div>
        </div>
      )}

      {/* Избранное */}
      {showFavorites && (
        <div>
          <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3">
            Избранное
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {favoriteTeas.slice(0, 4).map((fav) => (
              <button
                key={fav.id}
                type="button"
                onClick={() => handleTeaSelect({
                  id: fav.productId,
                  name: fav.product?.name,
                  imageUrl: fav.product?.imageUrl || undefined,
                })}
                className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white dark:bg-dark-card border border-silk dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-elevated transition-colors"
              >
                <OptimizedImage
                  src={fav.product?.imageUrl || '/placeholder-product.svg'}
                  alt={fav.product?.name || 'Чай'}
                  preset="thumb"
                  wrapperClassName="w-14 h-14 rounded-xl bg-gray-100 dark:bg-dark-elevated"
                  className="object-cover"
                />
                <div className="text-center">
                  <div className="text-sm font-medium text-ink dark:text-white line-clamp-1">
                    {fav.product?.name || 'Чай'}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Результаты поиска */}
      {showSearchResults && (
        <div>
          <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3">
            {searchQuery 
              ? `Результаты (${searchResults.length})` 
              : `${categories.find(c => c.id === selectedCategory)?.name || 'Чай'} (${searchResults.length})`
            }
          </h3>
          
          {isSearching ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full rounded-xl" />
              ))}
            </div>
          ) : searchResults.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              Ничего не найдено
            </div>
          ) : (
            <div className="space-y-2">
              {searchResults.map((tea) => (
                <TeaCard
                  key={tea.id}
                  name={tea.name}
                  category={tea.categoryName}
                  subcategory={tea.subcategoryName}
                  imageUrl={tea.images?.[0]?.url}
                  onClick={() => handleTeaSelect({
                    id: tea.id,
                    name: tea.name,
                    category: tea.categoryName,
                    subcategory: tea.subcategoryName,
                    imageUrl: tea.images?.[0]?.url,
                  })}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Карточка чая в результатах поиска
 */
interface TeaCardProps {
  name: string
  category?: string
  subcategory?: string
  subtitle?: string
  imageUrl?: string
  onClick: () => void
}

function TeaCard({ name, category, subcategory, subtitle, imageUrl, onClick }: TeaCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 p-3',
        'bg-white dark:bg-dark-card rounded-xl',
        'border border-silk dark:border-dark-border',
        'hover:bg-gray-50 dark:hover:bg-dark-elevated',
        'transition-colors active:scale-[0.98]'
      )}
    >
      {imageUrl ? (
        <OptimizedImage
          src={imageUrl}
          alt={name}
          preset="thumb"
          wrapperClassName="w-12 h-12 rounded-xl flex-shrink-0 bg-gray-100 dark:bg-dark-elevated"
          className="object-cover"
        />
      ) : (
        <div className="w-12 h-12 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center flex-shrink-0">
          <Coffee className="w-5 h-5 text-primary dark:text-primary-light" />
        </div>
      )}
      
      <div className="flex-1 min-w-0 text-left">
        <div className="font-medium text-ink dark:text-white truncate">
          {name}
        </div>
        {/* Категория / подкатегория */}
        {(category || subcategory) && (
          <div className="text-xs text-primary dark:text-primary-light truncate">
            {subcategory ? `${category} · ${subcategory}` : category}
          </div>
        )}
        {/* Дополнительная подпись (дата и т.п.) */}
        {subtitle && !category && !subcategory && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {subtitle}
          </div>
        )}
      </div>
      
      <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
    </button>
  )
}

export default TeaSearchContent

